import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import { useInviteUser } from '@/hooks/useInviteUser';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { useUserOrganizationRole } from '@/hooks/useUserRole';
import { getUserRole } from '@/lib/auth/token';
import { canInvite } from '@/lib/roleMapping';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './ui/form';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
const formSchema = z.object({
  email: z.string().email('Veuillez entrer une adresse email valide'),
  role: z.enum(['ADMIN', 'EDITOR', 'VIEWER'], {
    required_error: 'Veuillez sélectionner un rôle',
  }),
});

type FormValues = z.infer<typeof formSchema>;

interface UserInviteFormProps {
  onSuccess?: () => void;
}

export function UserInviteForm({ onSuccess }: UserInviteFormProps) {
  const { data: organization } = useCurrentOrganization();
  const { mutate: inviteUser, isPending: isInvitingUser } = useInviteUser();
  const userRole = getUserRole();
  const userOrganizationRole = useUserOrganizationRole();

  const canInviteUsers = canInvite(userRole, userOrganizationRole);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      role: 'VIEWER',
    },
  });

  if (!canInviteUsers) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">
          Vous n'avez pas les permissions pour inviter des utilisateurs.
        </p>
      </div>
    );
  }

  async function onSubmit(data: FormValues) {
    if (!organization) {
      return;
    }
    inviteUser(
      {
        organizationId: organization.id,
        email: data.email,
        role: data.role,
      },
      {
        onSuccess: () => {
          form.reset();
          toast.success('Invitation envoyée avec succès');
          onSuccess?.();
        },
      }
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder="Entrez l'adresse email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Role</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="ADMIN">Administrateur</SelectItem>
                  <SelectItem value="EDITOR">Éditeur</SelectItem>
                  <SelectItem value="VIEWER">Lecteur</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />

              {/* Description dynamique selon le rôle */}
              <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                  Permissions du rôle{' '}
                  {field.value === 'ADMIN'
                    ? 'Administrateur'
                    : field.value === 'EDITOR'
                    ? 'Éditeur'
                    : 'Lecteur'}{' '}
                  :
                </h4>
                <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  {field.value === 'ADMIN' && (
                    <>
                      <p>
                        ✅ <strong>Accès complet</strong> à toutes les
                        fonctionnalités
                      </p>
                      <p>
                        ✅ Peut gérer les lignes, forfaits, options et commandes
                      </p>
                      <p>✅ Peut ajouter/modifier/supprimer des adresses</p>
                      <p>✅ Peut inviter de nouveaux utilisateurs</p>
                      <p>✅ Peut générer une commande</p>
                    </>
                  )}
                  {field.value === 'EDITOR' && (
                    <>
                      <p>
                        ✅ <strong>Mêmes droits que l'Administrateur</strong>{' '}
                        sauf pour les utilisateurs
                      </p>
                      <p>
                        ✅ Peut gérer les lignes, forfaits, options et commandes
                      </p>
                      <p>✅ Peut ajouter/modifier/supprimer des adresses</p>
                      <p>✅ Peut générer une commande</p>
                      <p>
                        ❌ <strong>Ne peut pas</strong> inviter de nouveaux
                        utilisateurs
                      </p>
                    </>
                  )}
                  {field.value === 'VIEWER' && (
                    <>
                      <p>
                        ✅ <strong>Lecture seule</strong> sur toutes les
                        informations
                      </p>
                      <p>
                        ✅ Peut voir les lignes, forfaits, commandes et adresses
                      </p>
                      <p>
                        ❌ <strong>Ne peut pas</strong> générer une commande
                      </p>
                      <p>
                        ❌ <strong>Ne peut pas</strong> gérer les utilisateurs
                      </p>
                    </>
                  )}
                </div>
              </div>
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={isInvitingUser || !form.formState.isValid}
        >
          {isInvitingUser ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Envoi en cours...
            </>
          ) : (
            "Envoyer l'invitation"
          )}
        </Button>
      </form>
    </Form>
  );
}
